import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { fetchStatsSummary } from '@/api/endpoints/stats';
import type { StatsSummary } from '@/types/stats';
import { extractAxiosMessage } from '@/utils/axiosError';

export type StatsSummaryStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseStatsSummaryValue {
  readonly status: StatsSummaryStatus;
  readonly data: StatsSummary | null;
  readonly error: string | null;
  readonly refresh: () => void;
}

export function useStatsSummary(): UseStatsSummaryValue {
  const [status, setStatus] = useState<StatsSummaryStatus>('idle');
  const [data, setData] = useState<StatsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    fetchStatsSummary()
      .then((payload) => {
        if (!cancelled) {
          setData(payload);
          setStatus('success');
        }
      })
      .catch((err: unknown) => {
        if (cancelled || axios.isCancel(err)) return;
        setError(extractAxiosMessage(err) ?? 'unknown error');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadCounter]);

  const refresh = useCallback(() => {
    setReloadCounter((value) => value + 1);
  }, []);

  return { status, data, error, refresh };
}
